import { ref, getDownloadURL } from 'firebase/storage';
import { useState, useEffect } from 'react';
import { storage } from "../firebaseConfig";


// ─── Singleton Service ────────────────────────────────────────────────────────

// const storage = getStorage(initializeApp(firebaseConfig));
const client = window.location.hostname.split('.')[0];

// Promise cache: deduplicates concurrent requests
const promiseCache = {};
// Resolved value cache: allows synchronous reads after first fetch
const resolvedCache = {};

/**
 * Prefetches an image into the browser cache so it renders instantly.
 */
function prefetchImage(url) {
    if (!url) return Promise.resolve();
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = resolve;
        img.src = url;
    });
}

/**
 * Fetches a logo URL from Firebase Storage.
 * Returns null if the logo does not exist.
 * Concurrent calls for the same name share the same promise.
 */
export function getLogo(name) {
    if (promiseCache[name] !== undefined) {
        return promiseCache[name];
    }

    const refPath = `PrePaidCardLogo/TA/logos/${client}/${name}`;
    const promise = getDownloadURL(ref(storage, refPath))
        .then((url) => {
            resolvedCache[name] = url;
            prefetchImage(url);
            return url;
        })
        .catch(() => {
            resolvedCache[name] = null;
            return null;
        });

    promiseCache[name] = promise;
    return promise;
}

/**
 * Preloads only big logos (needed on login/registration pages).
 * Also prefetches the actual image binaries into browser cache.
 */
export async function preloadBigLogos() {
    const names = ['logo1-big.png', 'logo2-big.png'];
    const urls = await Promise.all(names.map(getLogo));
    await Promise.all(urls.filter(Boolean).map(prefetchImage));
}

/**
 * Preloads small logos (needed on dashboard sidebar).
 * Also prefetches the actual image binaries into browser cache.
 */
export async function preloadSmallLogos() {
    const names = ['logo1-small.png', 'logo2-small.png'];
    const urls = await Promise.all(names.map(getLogo));
    await Promise.all(urls.filter(Boolean).map(prefetchImage));
}

// ─── React Hook ──────────────────────────────────────────────────────────────

/**
 * Hook to fetch a single logo URL by name.
 *
 * If the logo was already fetched (e.g. by preloadBigLogos),
 * the hook returns the URL SYNCHRONOUSLY on first render — no loading flash.
 */
export function useLogo(name) {
    // Check resolved cache synchronously — skip loading state entirely if cached
    const cached = name && resolvedCache[name] !== undefined ? resolvedCache[name] : undefined;
    const [url, setUrl] = useState(cached);
    const [loading, setLoading] = useState(cached === undefined);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!name) return;

        // Already have the resolved value — no fetch needed
        if (resolvedCache[name] !== undefined) {
            setUrl(resolvedCache[name]);
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        getLogo(name).then((result) => {
            if (!cancelled) {
                setUrl(result);
                setLoading(false);
            }
        }).catch((err) => {
            if (!cancelled) {
                setError(err);
                setLoading(false);
            }
        });

        return () => { cancelled = true; };
    }, [name]);

    return { url, loading, error };
}